import type { Circuit, TrackCell, ValidationResult } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import { applyJokerLapToGrid, validateJokerLap } from './jokerLap.js';
import { applyStartLaneToGrid, validateStartLane } from './startLane.js';
import { pruneDecorCells } from './circuitDecorEdit.js';
import { pruneCircuitProps } from './circuitPropEdit.js';
import { listAllAmbientCoords } from './circuitOccupancy.js';
import { pruneCellStyles } from './circuitSurfaceStyle.js';
import { pruneRoadPieces, validateRoadPieces } from './circuitRoadPieceEdit.js';

/** Longueur minimale d'une run consécutive de cases TURN (virage). */
export const MIN_TURN_RUN_LENGTH = 3;

export type PaintableCellType = TrackCellType.STRAIGHT | TrackCellType.TURN | TrackCellType.START;

export interface CellTypeCounts {
  straights: number;
  turns: number;
  starts: number;
}

export function isPathClosed(path: TrackCell[]): boolean {
  if (path.length < 2) return false;
  const first = path[0];
  const last = path[path.length - 1];
  return first.x === last.x && first.y === last.y;
}

/** Chemin sans la case de fermeture dupliquée. */
export function getCorePath(path: TrackCell[]): TrackCell[] {
  if (path.length === 0) return [];
  if (isPathClosed(path)) return path.slice(0, -1);
  return [...path];
}

export interface CircuitPathBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  widthCells: number;
  heightCells: number;
}

/** Boîte englobante du tracé (sans la case de fermeture dupliquée). */
export function getCircuitPathBounds(path: TrackCell[]): CircuitPathBounds | null {
  const core = getCorePath(path);
  if (core.length === 0) return null;

  let minX = core[0].x;
  let maxX = core[0].x;
  let minY = core[0].y;
  let maxY = core[0].y;
  for (const cell of core) {
    minX = Math.min(minX, cell.x);
    maxX = Math.max(maxX, cell.x);
    minY = Math.min(minY, cell.y);
    maxY = Math.max(maxY, cell.y);
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    widthCells: maxX - minX + 1,
    heightCells: maxY - minY + 1,
  };
}

/** Boîte englobante de tout le contenu visible (tracé, extensions, décor). */
export function getFullCircuitBounds(circuit: Circuit, margin = 0): CircuitPathBounds | null {
  const coords: { x: number; y: number }[] = [];
  for (const cell of circuit.path) {
    coords.push({ x: cell.x, y: cell.y });
  }
  for (const cell of circuit.jokerLap?.cells ?? []) {
    coords.push({ x: cell.x, y: cell.y });
  }
  for (const cell of circuit.startLane?.cells ?? []) {
    coords.push({ x: cell.x, y: cell.y });
  }
  coords.push(...listAllAmbientCoords(circuit));

  if (coords.length === 0) return null;

  let minX = coords[0].x;
  let maxX = coords[0].x;
  let minY = coords[0].y;
  let maxY = coords[0].y;
  for (const cell of coords) {
    minX = Math.min(minX, cell.x);
    maxX = Math.max(maxX, cell.x);
    minY = Math.min(minY, cell.y);
    maxY = Math.max(maxY, cell.y);
  }

  minX = Math.max(0, minX - margin);
  minY = Math.max(0, minY - margin);
  maxX = Math.min(circuit.width - 1, maxX + margin);
  maxY = Math.min(circuit.height - 1, maxY + margin);

  return {
    minX,
    minY,
    maxX,
    maxY,
    widthCells: maxX - minX + 1,
    heightCells: maxY - minY + 1,
  };
}

/** Boîte englobante du tracé principal + extensions (cadrage jeu / replay). */
export function getCircuitDisplayBounds(circuit: Circuit): CircuitPathBounds | null {
  if (circuit.path.length === 0) return null;
  return getFullCircuitBounds(circuit, 0);
}

/**
 * Retourne les longueurs de chaque run consécutive de cases TURN dans le path.
 * Pour un circuit fermé, le dernier point (dupliqué) n'est pas compté.
 */
export function getTurnRuns(path: TrackCell[]): number[] {
  if (path.length === 0) return [];
  const lastIndex = isPathClosed(path) ? path.length - 1 : path.length;
  const runs: number[] = [];
  let run = 0;
  for (let i = 0; i < lastIndex; i++) {
    if (path[i].type === TrackCellType.TURN) {
      run++;
    } else {
      if (run > 0) {
        runs.push(run);
        run = 0;
      }
    }
  }
  if (run > 0) runs.push(run);
  return runs;
}

export function countCellTypes(path: TrackCell[]): CellTypeCounts {
  const core = getCorePath(path);
  let straights = 0;
  let turns = 0;
  let starts = 0;
  for (const cell of core) {
    if (cell.type === TrackCellType.STRAIGHT) straights++;
    else if (cell.type === TrackCellType.TURN) turns++;
    else if (cell.type === TrackCellType.START) starts++;
  }
  return { straights, turns, starts };
}

/**
 * Compte les segments consécutifs (comme à la génération), pas les cases individuelles.
 * START et STRAIGHT appartiennent au même type de segment « ligne droite ».
 */
export function countPathSegmentTypes(path: TrackCell[]): { straights: number; turns: number } {
  const core = getCorePath(path);
  if (core.length === 0) return { straights: 0, turns: 0 };

  const segmentKind = (type: TrackCellType): 'straight' | 'turn' =>
    type === TrackCellType.TURN ? 'turn' : 'straight';

  let straightRuns = 0;
  let turnRuns = 0;
  let currentKind = segmentKind(core[0].type);
  if (currentKind === 'straight') straightRuns = 1;
  else turnRuns = 1;

  for (let i = 1; i < core.length; i++) {
    const kind = segmentKind(core[i].type);
    if (kind !== currentKind) {
      if (kind === 'turn') turnRuns++;
      else straightRuns++;
      currentKind = kind;
    }
  }

  return { straights: straightRuns, turns: turnRuns };
}

export function applyPathCellType(
  path: TrackCell[],
  pathIndex: number,
  type: PaintableCellType
): TrackCell[] {
  const next = path.map((cell) => ({ ...cell }));
  const coreLen = getCorePath(next).length;
  if (pathIndex < 0 || pathIndex >= coreLen) return next;

  if (type === TrackCellType.START) {
    for (let i = 0; i < coreLen; i++) {
      if (i !== pathIndex && next[i].type === TrackCellType.START) {
        next[i] = { ...next[i], type: TrackCellType.STRAIGHT };
      }
    }
  }

  next[pathIndex] = { ...next[pathIndex], type };
  return next;
}

export function rotatePathToStart(path: TrackCell[]): TrackCell[] {
  if (path.length === 0) return path;

  const closed = isPathClosed(path);
  const core = (closed ? path.slice(0, -1) : path).map((cell) => ({ ...cell }));

  let startIdx = core.findIndex((cell) => cell.type === TrackCellType.START);
  if (startIdx < 0) {
    core[0] = { ...core[0], type: TrackCellType.START };
    startIdx = 0;
  }

  let rotated = core;
  if (startIdx > 0) {
    rotated = [...core.slice(startIdx), ...core.slice(0, startIdx)];
  }

  for (let i = 0; i < rotated.length; i++) {
    if (i === 0) {
      rotated[0] = { ...rotated[0], type: TrackCellType.START };
    } else if (rotated[i].type === TrackCellType.START) {
      rotated[i] = { ...rotated[i], type: TrackCellType.STRAIGHT };
    }
  }

  if (closed) {
    return [...rotated, { x: rotated[0].x, y: rotated[0].y, type: TrackCellType.STRAIGHT }];
  }
  return rotated;
}

export function rebuildGridFromPath(circuit: Circuit): TrackCellType[][] {
  const grid = Array(circuit.height)
    .fill(null)
    .map(() => Array(circuit.width).fill(TrackCellType.EMPTY));

  for (const cell of getCorePath(circuit.path)) {
    if (cell.x >= 0 && cell.x < circuit.width && cell.y >= 0 && cell.y < circuit.height) {
      grid[cell.y][cell.x] = cell.type;
    }
  }
  return grid;
}

export function finalizeEditedCircuit(circuit: Circuit): Circuit {
  const joinCoord = pathIndexToCoord(circuit.path, circuit.startLane?.joinPathIndex);
  const jokerEntryCoord = pathIndexToCoord(circuit.path, circuit.jokerLap?.entryPathIndex);
  const jokerExitCoord = pathIndexToCoord(circuit.path, circuit.jokerLap?.exitPathIndex);

  const path = rotatePathToStart(circuit.path);
  let next: Circuit = { ...circuit, path };

  if (next.startLane && joinCoord) {
    const joinPathIndex = findPathIndexAt(next, joinCoord.x, joinCoord.y);
    if (joinPathIndex !== null) {
      next = { ...next, startLane: { ...next.startLane, joinPathIndex } };
    }
  }

  if (next.jokerLap) {
    const entryPathIndex = jokerEntryCoord
      ? (findPathIndexAt(next, jokerEntryCoord.x, jokerEntryCoord.y) ??
        next.jokerLap.entryPathIndex)
      : next.jokerLap.entryPathIndex;
    const exitPathIndex = jokerExitCoord
      ? (findPathIndexAt(next, jokerExitCoord.x, jokerExitCoord.y) ?? next.jokerLap.exitPathIndex)
      : next.jokerLap.exitPathIndex;
    next = { ...next, jokerLap: { ...next.jokerLap, entryPathIndex, exitPathIndex } };
  }

  const withGrid = {
    ...next,
    grid: rebuildGridFromPath(next),
  };
  const withJoker = { ...withGrid, grid: applyJokerLapToGrid(withGrid) };
  const withStartLane = { ...withJoker, grid: applyStartLaneToGrid(withJoker) };
  return pruneRoadPieces(pruneCircuitProps(pruneDecorCells(pruneCellStyles(withStartLane))));
}

function pathIndexToCoord(
  path: TrackCell[],
  index: number | undefined
): { x: number; y: number } | null {
  if (index === undefined || index < 0) return null;
  const cell = path[index];
  if (!cell) return null;
  return { x: cell.x, y: cell.y };
}

export function pathHasNoOverlap(path: TrackCell[]): boolean {
  if (path.length === 0) return true;

  const visited = new Set<string>();
  const endIndex = isPathClosed(path) ? path.length - 1 : path.length;

  for (let i = 0; i < endIndex; i++) {
    const cell = path[i];
    const key = `${cell.x},${cell.y}`;
    if (visited.has(key)) return false;
    visited.add(key);
  }
  return true;
}

export function validateManualCircuitEdit(circuit: Circuit): ValidationResult {
  const errors: string[] = [];

  if (!isPathClosed(circuit.path)) {
    errors.push('Le circuit doit être fermé (commencer et finir au même point)');
  }

  if (!pathHasNoOverlap(circuit.path)) {
    errors.push('Le circuit ne doit pas avoir de chevauchement');
  }

  const jokerValidation = validateJokerLap(circuit);
  if (!jokerValidation.isValid) {
    errors.push(...jokerValidation.errors);
  }

  const startLaneValidation = validateStartLane(circuit);
  if (!startLaneValidation.isValid) {
    errors.push(...startLaneValidation.errors);
  }

  const roadPiecesValidation = validateRoadPieces(circuit);
  if (!roadPiecesValidation.isValid) {
    errors.push(...roadPiecesValidation.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function findPathIndexAt(circuit: Circuit, x: number, y: number): number | null {
  const core = getCorePath(circuit.path);
  for (let i = 0; i < core.length; i++) {
    if (core[i].x === x && core[i].y === y) return i;
  }
  return null;
}

export function cellCoordKey(x: number, y: number): string {
  return `${x},${y}`;
}

/** Cases d’un run TURN plus court que MIN_TURN_RUN_LENGTH. */
export function getShortTurnRunCells(path: TrackCell[]): { x: number; y: number }[] {
  const core = getCorePath(path);
  const cells: { x: number; y: number }[] = [];
  let runStart = 0;
  let runLen = 0;

  const flush = (): void => {
    if (runLen > 0 && runLen < MIN_TURN_RUN_LENGTH) {
      for (let i = runStart; i < runStart + runLen; i++) {
        cells.push({ x: core[i].x, y: core[i].y });
      }
    }
    runLen = 0;
  };

  for (let i = 0; i < core.length; i++) {
    if (core[i].type === TrackCellType.TURN) {
      if (runLen === 0) runStart = i;
      runLen++;
    } else {
      flush();
    }
  }
  flush();
  return cells;
}

function getOverlappingPathCells(path: TrackCell[]): { x: number; y: number }[] {
  const lastIndex = isPathClosed(path) ? path.length - 1 : path.length;
  const counts = new Map<string, number>();

  for (let i = 0; i < lastIndex; i++) {
    const key = cellCoordKey(path[i].x, path[i].y);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const cells: { x: number; y: number }[] = [];
  for (const [key, count] of counts) {
    if (count > 1) {
      const [x, y] = key.split(',').map(Number);
      cells.push({ x, y });
    }
  }
  return cells;
}

/** Cases du tracé à surligner en rouge (chevauchements, virages courts, tracé ouvert). */
export function getCircuitEditErrorCells(circuit: Circuit): { x: number; y: number }[] {
  const keys = new Set<string>();
  const path = circuit.path;

  for (const cell of getOverlappingPathCells(path)) {
    keys.add(cellCoordKey(cell.x, cell.y));
  }

  for (const cell of getShortTurnRunCells(path)) {
    keys.add(cellCoordKey(cell.x, cell.y));
  }

  // Tracé ouvert : seule l’extrémité libre (pas le départ, ni la 1ʳᵉ case seule).
  if (path.length > 1 && !isPathClosed(path)) {
    const last = path[path.length - 1];
    const start = path[0];
    if (last.x !== start.x || last.y !== start.y) {
      keys.add(cellCoordKey(last.x, last.y));
    }
  }

  return [...keys].map((key) => {
    const [x, y] = key.split(',').map(Number);
    return { x, y };
  });
}
